import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Button,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Link } from 'react-admin';
import { clsx } from 'clsx';

import { reportsHttp } from '@network';
import { ButtonLinkWidget, InitialCard } from '@components';
import { Credit } from '@types';
import { useSettingsContext } from '@providers/settings-provider';
import { dateFormatter, querySerialize } from '@utils';

interface Props {
  range: string;
  setLoader: (num: number, value: boolean) => void;
}

export const NewDocumentsUploadedReport = ({ range, setLoader }: Props) => {
  const { admin } = useSettingsContext();
  const [credits, setCredits] = useState<Credit[]>([]);
  const cls = useStyles();

  const loadCredits = async () => {
    setLoader(13, true);
    try {
      const nextCredits = await reportsHttp.creditsLastModification({ range });
      setCredits(nextCredits);
    } catch (e) {
      console.log(e);
    } finally {
      setLoader(13, false);
    }
  };

  useEffect(() => {
    if (admin) {
      loadCredits();
    }
  }, [range, admin]);

  const linkTo = '/credit?' + querySerialize({
    order: 'DESC',
    sort: 'modificationAt',
  });

  return (
    <InitialCard
      left="New Documents Uploaded"
      extraPadding fullHeight
      rightMessage="Not affected by time filter"
      rightTooltipColor={'#042E6B'}
      rightIcon={
        <svg
          xmlns="http://www.w3.org/2000/svg"
          height="24px"
          viewBox="0 -960 960 960"
          width="24px"
          fill="#F38336">
          <path d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 27-3 53t-10 51q-14-16-32.5-27T794-418q3-15 4.5-30.5T800-480q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93q51 0 97.5-15t85.5-42q12 17 29.5 30t37.5 20q-51 41-114.5 64T480-80Zm290-160q-21 0-35.5-14.5T720-290q0-21 14.5-35.5T770-340q21 0 35.5 14.5T820-290q0 21-14.5 35.5T770-240Zm-158-52L440-464v-216h80v184l148 148-56 56Z"/>
        </svg>
      }
    >
      <br />
      <TableContainer className="RaDatagrid-tableWrapper">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className={cls.thead}>Code</TableCell>
              <TableCell className={clsx(cls.thead, cls.theadUser)}>User</TableCell>
              <TableCell className={cls.thead}>Salesforce Link</TableCell>
              <TableCell className={cls.thead}>Last update</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {credits.map((credit) => (
              <TableRow key={credit.id}>
                <TableCell className={cls.value}>
                  <Link to={`/credit/${credit.id}?initial`} className={cls.link}>
                    {credit.code}
                  </Link>
                </TableCell>
                <TableCell className={cls.value}>
                  <Link to={`/user/${credit.user.id}?initial`} className={cls.link}>
                    {credit.user.fullName}
                  </Link>
                </TableCell>
                <TableCell className={clsx(cls.value, cls.center)}>
                  {credit.user.sfAccountLink && (
                    <ButtonLinkWidget className={cls.sfLink} text="SalesForce" link={credit.user.sfAccountLink} />
                  )}
                </TableCell>
                <TableCell className={clsx(cls.value, cls.center)}>
                  {credit.modificationAt ? dateFormatter.toDateSlash(credit.modificationAt) : '--'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Link to={linkTo}>
        <Button variant="contained" color="secondary" className={cls.btn}>
          View All
        </Button>
      </Link>
    </InitialCard>
  );
};

const useStyles = makeStyles({
  thead: {
    width: '20%',
    minWidth: 'auto !important',
  },
  theadUser: {
    width: '40% !important',
  },
  value: {
    color: '#74706B',
    fontSize: 11,
    fontWeight: 400,
    height: 39,
  },
  center: {
    textAlign: 'center',
  },
  link: {
    color: '#74706B',
    fontSize: 11,
    fontWeight: 400,
  },
  btn: {
    marginTop: 20,
    padding: '10px 95px !important',
    height: 40,
    color: '#FFF',
    fontSize: 15,
    fontWeight: 400,
    borderRadius: 8,
    textTransform: 'none',
  },
  sfLink: {
    color: '#fff',
    background: '#2196F3',

    '&:hover': {
      background: '#2196F3 !important',
    },
  },
});
